import { displayName, localMonthKey } from '@schwrz/shared';
import type { WorkspaceUserDto } from '@schwrz/shared';
import { computed, ref, toValue, watch, type MaybeRef } from 'vue';

import { api } from '../api/client';

/** Per-month allocated hours by workspace user id (all task statuses). */
const cacheByMonth = ref<Map<string, Map<string, number>>>(new Map());
const capByMonth = ref<Map<string, number | null>>(new Map());
const loadPromises = new Map<string, Promise<void>>();

/**
 * Assignee dropdown capacity hints — must mirror `assertAssigneeBudget` in
 * `apps/api/src/lib/task-budget.ts` (allocation month, all statuses).
 */
export function useAssigneeCapacity(monthKey: MaybeRef<string> = localMonthKey()) {
  const resolvedMonthKey = computed(() => toValue(monthKey));

  const activeMap = computed(() => {
    return cacheByMonth.value.get(resolvedMonthKey.value) ?? new Map<string, number>();
  });

  async function loadMonth(key: string): Promise<void> {
    const existing = loadPromises.get(key);
    if (existing) return existing;

    const promise = (async () => {
      try {
        const res = await api<{
          rows: { userId: string; allocatedHours: number }[];
          assigneeMonthlyHoursCap: number | null;
        }>(`/api/reports/user-hours?month=${encodeURIComponent(key)}`);
        const map = new Map<string, number>();
        for (const row of res.rows) {
          map.set(row.userId, row.allocatedHours);
        }
        cacheByMonth.value = new Map(cacheByMonth.value).set(key, map);
        capByMonth.value = new Map(capByMonth.value).set(key, res.assigneeMonthlyHoursCap ?? null);
      } catch {
        cacheByMonth.value = new Map(cacheByMonth.value).set(key, new Map());
      } finally {
        loadPromises.delete(key);
      }
    })();

    loadPromises.set(key, promise);
    return promise;
  }

  watch(
    resolvedMonthKey,
    (key) => {
      void loadMonth(key);
    },
    { immediate: true },
  );

  /** Clear cached fetch and reload the active month (e.g. after create/update). */
  function refresh(): Promise<void> {
    const key = resolvedMonthKey.value;
    loadPromises.delete(key);
    return loadMonth(key);
  }

  function assigneeLabel(user: Pick<WorkspaceUserDto, 'id' | 'firstName' | 'lastName'>): string {
    const name = displayName(user);
    const allocated = activeMap.value.get(user.id);
    if (allocated === undefined) return name;
    const cap = capByMonth.value.get(resolvedMonthKey.value) ?? null;
    if (cap === null) {
      const rounded = Math.round(allocated * 10) / 10;
      return `${name} · ${rounded} ч в плане`;
    }
    const remaining = Math.max(0, cap - allocated);
    if (remaining <= 0) return `${name} · лимит исчерпан`;
    const rounded = Math.round(remaining * 10) / 10;
    return `${name} · свободно ${rounded} ч`;
  }

  return { load: () => loadMonth(resolvedMonthKey.value), refresh, assigneeLabel, activeMap };
}
